import { Extension } from "@tiptap/core";
import posthog from "posthog-js";

import type { EmailThreadScope } from "@/types/email";

export type EmailComposerSource =
  | "toolbar"
  | "keyboard_shortcut"
  | "drag_drop"
  | "paste"
  | "bubble_menu"
  | "block_bubble_menu"
  | "resize"
  | "selection"
  | "slash_command";

export function trackEmailComposerAction(
  action: string,
  source: EmailComposerSource,
  properties?: Record<string, unknown>,
) {
  posthog.capture(`email_composer_${action}`, { source, ...properties });
}

export type EmailSendComposerType = "compose" | "reply" | "forward";

/**
 * Fired after a successful send from compose/reply/forward. Compares the
 * sent values against the clinic-default values the "From" field was
 * pre-filled with, so `*_overridden` reflects a real per-message edit
 * rather than just "a value was present" (the fields are always pre-filled).
 *
 * A field cleared to blank is *not* an override: the composers omit a blank
 * `senderName`/`fromLocalPart` from the send payload entirely, and the
 * backend falls back to the same clinic default the field was pre-filled
 * with — so the effective sent value still equals the default.
 *
 * `threadScope` distinguishes the shared clinic mailbox from the
 * per-provider patient compose modal / chat-timeline email thread, which
 * fire this same event — without it, mailbox adoption can't be isolated
 * from those pre-existing send paths in analytics.
 */
export function trackEmailSentWithSenderConfig(
  composerType: EmailSendComposerType,
  senderName: string,
  defaultSenderName: string,
  localPart: string,
  defaultLocalPart: string,
  threadScope: EmailThreadScope,
) {
  const trimmedSenderName = senderName.trim();
  const trimmedLocalPart = localPart.trim();
  posthog.capture("email_sent_with_sender_config", {
    composer_type: composerType,
    sender_name_overridden:
      trimmedSenderName !== "" &&
      trimmedSenderName !== defaultSenderName.trim(),
    local_part_overridden:
      trimmedLocalPart !== "" &&
      trimmedLocalPart.toLowerCase() !== defaultLocalPart.trim().toLowerCase(),
    thread_scope: threadScope,
  });
}

/**
 * Tiptap extension that intercepts built-in keyboard shortcuts to fire
 * analytics events. Returns `false` so the real extension handler still runs.
 *
 * Must be registered with high priority so it runs before the mark extensions.
 */
export const EmailComposerKeyboardAnalytics = Extension.create({
  name: "emailComposerKeyboardAnalytics",
  priority: 1000,

  addKeyboardShortcuts() {
    const track = (action: string) => {
      trackEmailComposerAction(action, "keyboard_shortcut");
      return false as const;
    };

    return {
      "Mod-b": () => track("bold_toggled"),
      "Mod-i": () => track("italic_toggled"),
      "Mod-u": () => track("underline_toggled"),
      "Mod-Shift-x": () => track("strikethrough_toggled"),
    };
  },
});
